import React, { FC, useEffect, useState, useDeferredValue, Suspense, useContext } from 'react';
import {
  Heading,
  Text,
  Box,
  ButtonAppearance,
  SideSheet,
  SideSheetSize,
  SideSheetFooter,
  SideSheetBody,
  Spinner,
  SpinnerSize,
  Icon,
  TextFieldSize,
  IconSize,
  ToggleSwitch,
  Button,
} from '@bechtel/gds';
import {
  SearchFieldComponentStyled,
  HeaderStyled,
  CloseStyled,
  FooterButtonStyled,
  ListStyled,
  SearchContainerBoxStyled,
  ColumnHeaderBoxStyled,
} from './styled';
import { t, Trans } from '@lingui/macro';
import { useRodRequestCaddies, useReturnCaddy } from '~/hooks';
import {
  RodRequestCaddyFilterByTerm,
  RodRequestCaddyFilterByTermInput,
  FilterOperator,
  RodRequestCaddyComplexFilterInput,
  RodRequestCaddy,
  ReturnCaddiesInput,
  RodRequestCaddyStatusCode,
  ReturnCaddiesError,
} from '@bechtel/types';
import { useSessionStorage, CacheKeys, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { get, map, filter } from 'lodash';
import { BulkReturnRodRequestCaddyItem } from './item';
import { FieldArray, Formik, FormikProps, FormikHelpers as FormikActions } from 'formik';
import { BulkReturnCaddyError } from './error';
import { ApolloError } from '@apollo/client';
import { RodRoomDataContext } from '~/utilities/context';

interface BulkReturnRodRequestCaddyProps {
  onClose: () => void;
}

interface FormikState {
  caddyIds?: ReturnCaddiesInput['ids'];
}

const initialValues: FormikState = {
  caddyIds: [],
};

export const BulkReturnRodRequestCaddy: FC<BulkReturnRodRequestCaddyProps> = ({ onClose }) => {
  const pageLimit = 20;
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDeferredValue(searchQuery?.trim());
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [
    returnCaddies,
    { loading: returnCaddyLoading, error: returnCaddyMutationError, data: returnCaddyData },
  ] = useReturnCaddy();
  const [
    getRodRequestCaddies,
    { loading, error, data, refetch, networkStatus, called, fetchMore },
  ] = useRodRequestCaddies(true);
  const rodRequestCaddyList: RodRequestCaddy[] = get(data, 'rodRequestCaddies.items', []);
  const rodRequestCaddyListTotalRecords = get(data, 'rodRequestCaddies.total', 0);
  const rodRequestCaddyListTotalPages = rodRequestCaddyListTotalRecords
    ? Math.ceil(rodRequestCaddyListTotalRecords / pageLimit)
    : 0;
  const rodRequestCaddyListCursor = get(data, 'rodRequestCaddies.cursor', 1);
  const filteredRodRequestCaddies = filter(
    rodRequestCaddyList,
    (item: RodRequestCaddy) => item.currentStatusI18n == RodRequestCaddyStatusCode.ISSUED
  );
  const bulkReturnCaddyError: ReturnCaddiesError[] =
    get(returnCaddyData, 'returnCaddies.erroredCaddies', []) || returnCaddyMutationError;
  const [returnCaddiesError, setReturnCaddiesError] = useState<ReturnCaddiesError[] | ApolloError | null>(null);

  useEffect(() => {
    if (bulkReturnCaddyError.length < 1) return;
    setReturnCaddiesError(bulkReturnCaddyError);
  }, [bulkReturnCaddyError]);

  useEffect(() => {
    if (!returnCaddyMutationError) return;
    setReturnCaddiesError(returnCaddyMutationError);
  }, [returnCaddyMutationError]);

  useEffect(() => {
    const rodRoomFilter: RodRequestCaddyFilterByTermInput | null = !showAllRooms
      ? {
          term: RodRequestCaddyFilterByTerm.ROD_ROOM_ID,
          value: selectedRodRoom?.id,
          operator: FilterOperator.EQUALS,
        }
      : null;
    const rodCaddiesComplexFilter: RodRequestCaddyComplexFilterInput = rodRoomFilter
      ? {
          and: [
            {
              ...rodRoomFilter,
            },
            {
              term: RodRequestCaddyFilterByTerm.CADDY_STATUS,
              value: RodRequestCaddyStatusCode.ISSUED,
              operator: FilterOperator.EQUALS,
            },
          ],
          or: [],
        }
      : {
          and: [
            {
              term: RodRequestCaddyFilterByTerm.CADDY_STATUS,
              value: RodRequestCaddyStatusCode.ISSUED,
              operator: FilterOperator.EQUALS,
            },
          ],
          or: [],
        };
    if (debouncedSearchQuery && debouncedSearchQuery.length >= 3) {
      rodCaddiesComplexFilter.or?.push({
        term: RodRequestCaddyFilterByTerm.CADDY_NUMBER,
        value: debouncedSearchQuery,
        operator: FilterOperator.CONTAINS,
      });
      rodCaddiesComplexFilter.or?.push({
        term: RodRequestCaddyFilterByTerm.WELDER,
        value: debouncedSearchQuery,
        operator: FilterOperator.FUZZY,
      });
    }

    getRodRequestCaddies({
      variables: {
        input: {
          complexFilter: rodCaddiesComplexFilter,
        },
        paging: {
          cursor: '1',
          limit: pageLimit,
        },
      },
    });
  }, [debouncedSearchQuery, showAllRooms]);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      rodRequestCaddyListCursor <= rodRequestCaddyListTotalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: rodRequestCaddyListCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  return (
    <>
      <SideSheet
        visible
        style={{
          boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
          maxWidth: '535px',
        }}
        size={SideSheetSize.MEDIUM}
        data-testid="bulk-return-side-sheet"
      >
        <Formik
          initialValues={initialValues}
          onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
            if (!values || !values.caddyIds || !values.caddyIds.length) return;
            await returnCaddies({
              variables: {
                input: {
                  ids: values.caddyIds,
                  returnedToRodRoomId: selectedRodRoom?.id,
                },
              },
            });
            actions.resetForm({ values: initialValues });
            actions.setSubmitting(false);
          }}
        >
          {({ isValid, dirty, handleSubmit, values }: FormikProps<FormikState>) => {
            return (
              <>
                <HeaderStyled title="" data-testid="header-container">
                  <Box justifyContent={{ sm: 'space-between' }} pl={{ sm: '200' }}>
                    <Box alignItems={{ sm: 'center' }}>
                      <Icon icon="check-double" size={IconSize.MEDIUM} fillColor="bechtel.blue.700" />
                      <Box pl={{ sm: '400' }} pr={{ sm: '550' }}>
                        <Text color="grey.1000" weight="600" size="600">
                          <Trans>Bulk Return</Trans>
                        </Text>
                      </Box>
                      <Box pl={{ sm: '550' }} alignItems={{ sm: 'center' }}>
                        <ToggleSwitch
                          isToggled={showAllRooms}
                          onToggle={() => {
                            setShowAllRooms(!showAllRooms);
                          }}
                        />
                        <Box pl={{ sm: '300' }}>
                          <Text color="bechtel.blue.700" size="400">
                            <Trans>All Rod Rooms</Trans>
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                    <CloseStyled data-testid="closed" onClick={() => onClose()} />
                  </Box>
                </HeaderStyled>
                <SideSheetBody data-testid="caddies-body-container">
                  <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} style={{ overflowY: 'hidden' }}>
                    <SearchContainerBoxStyled data-testid="caddies-search-container">
                      <Box flexBasis={{ sm: '50%' }}>
                        <SearchFieldComponentStyled
                          size={TextFieldSize.LARGE}
                          iconSize={IconSize.MEDIUM}
                          placeholder={t`Search Caddies/Welders`}
                          type="search"
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                          }}
                          value={searchQuery}
                        />
                      </Box>
                      <Box
                        flexBasis={{ sm: '50%' }}
                        justifyContent={{ sm: 'flex-end' }}
                        alignItems={{ sm: 'center' }}
                        pr={{ sm: '600' }}
                      >
                        <Text color="blue.700" weight="600" size="600">
                          {/* TODO: Rodroom stats integration*/}
                          43/102 &nbsp;
                        </Text>
                        <Text color="blue.700" size="300">
                          <Trans>Caddies Returned</Trans>
                        </Text>
                      </Box>
                    </SearchContainerBoxStyled>
                    <ColumnHeaderBoxStyled>
                      {/* Do not delete this emptybox placeholder to alin child column */}
                      <Box flexBasis={{ sm: '5%' }}></Box>
                      <Box flexBasis={{ sm: '45%' }} alignItems={{ sm: 'center' }}>
                        <Text size="400" weight="600" color="grey.700">
                          <Trans>Caddies</Trans>
                        </Text>
                      </Box>
                      <Box flexBasis={{ sm: '50%' }} alignItems={{ sm: 'center' }}>
                        <Text size="400" weight="600" color="grey.700">
                          <Trans>Allocated Welders</Trans>
                        </Text>
                      </Box>
                    </ColumnHeaderBoxStyled>
                    <ListStyled
                      onScroll={(e) => {
                        handleScroll(e);
                      }}
                    >
                      <FieldArray name="caddyIds">
                        {(fieldArrayProps) => {
                          return (
                            <>
                              {filteredRodRequestCaddies &&
                                filteredRodRequestCaddies?.length > 0 &&
                                map(filteredRodRequestCaddies, (rodRequestCaddy: RodRequestCaddy) => {
                                  return (
                                    <ErrorBoundary>
                                      <BulkReturnRodRequestCaddyItem
                                        item={rodRequestCaddy}
                                        key={rodRequestCaddy.id}
                                        fieldArrayProps={fieldArrayProps}
                                      />
                                    </ErrorBoundary>
                                  );
                                })}
                            </>
                          );
                        }}
                      </FieldArray>
                      {loading && (
                        <Box
                          data-testid="loading-caddies"
                          p={{ sm: '500' }}
                          alignItems={{ sm: 'center' }}
                          justifyContent={{ sm: 'center' }}
                          flexGrow={{ sm: 1 }}
                          css={rodRequestCaddyList.length === 0 ? 'height: 100%;' : ''}
                        >
                          <Spinner size={SpinnerSize.LARGE} />
                        </Box>
                      )}
                      {!loading && error && (
                        <Box
                          alignItems={{ sm: 'center' }}
                          justifyContent={{ sm: 'center' }}
                          flexGrow={{ sm: 1 }}
                          flexDirection={{ sm: 'column' }}
                          p={{ sm: '500' }}
                          data-testid="return-caddies-error"
                        >
                          <Box pb={{ sm: '500' }}>
                            <Text>
                              <Trans>
                                There was a problem loading Rod Caddies List. Please click the button below to try
                                again.
                              </Trans>
                            </Text>
                          </Box>
                          <Button onClick={() => refetch()}>
                            <Box mr={{ sm: '400' }}>
                              <Icon icon="undo" />
                            </Box>
                            <Trans>Retry</Trans>
                          </Button>
                        </Box>
                      )}
                      {called && !loading && !error && rodRequestCaddyList?.length === 0 && (
                        <Box
                          flexDirection={{
                            sm: 'column',
                          }}
                          alignItems={{
                            sm: 'center',
                          }}
                          pt={{ sm: '900' }}
                        >
                          <Heading size="400" weight="600" color="grey.600">
                            <Trans> No Caddies found.</Trans>
                          </Heading>
                          <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                            <Text
                              size="400"
                              family="graphik"
                              color="grey.600"
                              style={{
                                textAlign: 'center',
                                lineHeight: '1.57',
                              }}
                            >
                              <Trans>Try changing your search criteria or editing your filters.</Trans>
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </ListStyled>
                  </Box>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
                        <Trans>Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <Box>
                      <FooterButtonStyled
                        data-testid="done-btn"
                        disabled={!isValid || !dirty || returnCaddyLoading}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={() => handleSubmit()}
                      >
                        {returnCaddyLoading && <Spinner />}
                        {!returnCaddyLoading && <Trans>Update Selected</Trans>}
                        {values.caddyIds && values.caddyIds.length > 0 ? ` (${values.caddyIds.length}) ` : ``}
                      </FooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </>
            );
          }}
        </Formik>
      </SideSheet>
      {(returnCaddiesError instanceof ApolloError || returnCaddiesError instanceof Array) && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <BulkReturnCaddyError errors={returnCaddiesError} close={() => setReturnCaddiesError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
